import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleSolidInfoIcon, CopyIcon } from '../../../../assets/icons';
import { buildDisplayText } from '../../../../utils';
import { useExecuteQuery } from '../../../hooks/useExecuteQuery';
import { GET_BANK_TRANSFER_REF } from '../../graphql/getBankTransferRef';

const defaultBankTransferInfo = [
  { type: 'company', title: 'Company', value: 'Cult Wines Ltd' },
  { type: 'sortCode', title: 'Sort Code', value: '40-03-21' },
  { type: 'accNumber', title: 'Account No.', value: '02276453' },
  { type: 'iban', title: 'IBAN', value: 'GB49HBUK40032102276453' },
  { type: 'bic', title: 'BIC/Swift', value: 'HBUKGB4B' },
  { type: 'bankName', title: 'Bank', value: 'HSBC' },
  { type: 'bankAddress', title: 'Branch address', value: '21 Kings Mall, King Street, London, W6 0QF' },
  { type: 'reference', title: 'Reference', value: '' },
];

enum DisplayTextKeys {
  TITLE_TEXT = 'title',
  SUBTITLE_TEXT = 'subTitle',
  COPIED_TEXT = 'copied_text',
  DISCLAIMER = 'disclaimer',
}

interface BankTransferTemplateProps {
  subTitleNode?: ReactNode;
  disclaimerNode?: ReactNode;
  bankTransferDetails?: Record<string, string>[];
  onBack?: () => void;
}

const BankTransferTemplate: FC<BankTransferTemplateProps> = ({ bankTransferDetails }) => {
  const { t } = useTranslation();
  const displayText = useMemo(
    () => buildDisplayText(Object.values(DisplayTextKeys), 'account:slideout.topup.bankTransfer', t),
    [t],
  );
  const [copied, setCopied] = useState<string[]>([]);
  const { results: paymentRef } = useExecuteQuery('paymentReferenceNumber', GET_BANK_TRANSFER_REF);
  useEffect(() => {
    if (copied.length > 0) {
      setTimeout(() => {
        setCopied((x) => {
          const newCopied = [...x];
          newCopied.shift();
          return newCopied;
        });
      }, 1000);
    }
  }, [copied]);

  const bankTransferInfo = useMemo(() => {
    return bankTransferDetails ?? defaultBankTransferInfo;
  }, [bankTransferDetails]);

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center flex-col py-8 px-5 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700">
        <div className="w-14 h-14 rounded-full bg-orange/10 dark:bg-teal-500/10 flex items-center justify-center mb-3">
          <span className="text-2xl">🏦</span>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {displayText[DisplayTextKeys.TITLE_TEXT]}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 text-center px-4">
          {displayText[DisplayTextKeys.SUBTITLE_TEXT]}
        </p>
      </div>

      {/* Bank Details Card */}
      <div className="flex-1 p-5 overflow-y-auto">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden">
          {bankTransferInfo.map((info, index) => {
            const { type, title, value } = info;
            const isCopied = copied.includes(type);
            const val = type === 'reference' ? paymentRef : value;
            const isLast = index === bankTransferInfo.length - 1;
            return (
              <div
                key={`${type}`}
                className={`flex w-full items-center p-4 ${
                  !isLast ? 'border-b border-gray-100 dark:border-slate-700' : ''
                }`}
              >
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{title}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white break-all">{String(val)}</p>
                </div>
                <button
                  className={`ml-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                    isCopied
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                  }`}
                  onClick={() => {
                    setCopied([...copied, type]);
                    navigator.clipboard.writeText(value);
                  }}
                >
                  {isCopied ? (
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Copied
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <CopyIcon className="w-3 h-3" />
                      Copy
                    </span>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Info Card */}
        <div className="mt-4 p-4 bg-blue-50 dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700">
          <div className="flex items-start gap-3">
            <CircleSolidInfoIcon className="text-blue-500 dark:text-teal-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900 dark:text-white text-sm">
                {t`account:slideout.topup.recurring_payment_info_title`}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{displayText[DisplayTextKeys.DISCLAIMER]}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankTransferTemplate;
